function findLastIndex(items, predicate) {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index], index)) return index;
  }
  return -1;
}

function newTaskId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `sync-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function bumpTask(existing, incoming, changes) {
  return {
    ...existing,
    ...incoming,
    ...changes,
    taskId: existing.taskId,
    ownerUserId: existing.ownerUserId,
    createdAt: existing.createdAt,
    updatedAt: Date.now(),
    revision: Number(existing.revision || 1) + 1,
  };
}

export function createTaskEnvelope(task, ownerUserId) {
  const now = Date.now();
  return {
    ...task,
    taskId: task.taskId || newTaskId(),
    ownerUserId: String(task.ownerUserId || ownerUserId || ""),
    createdAt: Number(task.createdAt || now),
    updatedAt: now,
    revision: Math.max(1, Number(task.revision || 1)),
  };
}

export function mergeTaskIntoQueue(queue, incomingTask) {
  const nextQueue = [...queue];
  const task = incomingTask;

  switch (task.type) {
    case "updatePoint": {
      const createIndex = findLastIndex(
        nextQueue,
        (item) => item.type === "createPoint" && item.data?.id === task.pointId
      );
      if (createIndex > -1) {
        nextQueue[createIndex] = bumpTask(nextQueue[createIndex], task, {
          type: "createPoint",
          data: { ...nextQueue[createIndex].data, ...(task.updates || {}) },
        });
        return nextQueue;
      }

      const updateIndex = findLastIndex(
        nextQueue,
        (item) => item.type === "updatePoint" && item.pointId === task.pointId
      );
      if (updateIndex > -1) {
        nextQueue[updateIndex] = bumpTask(nextQueue[updateIndex], task, {
          updates: {
            ...(nextQueue[updateIndex].updates || {}),
            ...(task.updates || {}),
          },
          baseUpdatedAt:
            nextQueue[updateIndex].baseUpdatedAt ?? task.baseUpdatedAt ?? null,
        });
        return nextQueue;
      }
      break;
    }

    case "updateTeam": {
      const createIndex = findLastIndex(
        nextQueue,
        (item) => item.type === "createTeam" && item.data?.id === task.teamId
      );
      if (createIndex > -1) {
        nextQueue[createIndex] = bumpTask(nextQueue[createIndex], task, {
          type: "createTeam",
          data: { ...nextQueue[createIndex].data, ...(task.data || {}) },
        });
        return nextQueue;
      }

      const updateIndex = findLastIndex(
        nextQueue,
        (item) => item.type === "updateTeam" && item.teamId === task.teamId
      );
      if (updateIndex > -1) {
        nextQueue[updateIndex] = bumpTask(nextQueue[updateIndex], task, {
          data: {
            ...(nextQueue[updateIndex].data || {}),
            ...(task.data || {}),
          },
          baseUpdatedAt:
            nextQueue[updateIndex].baseUpdatedAt ?? task.baseUpdatedAt ?? null,
        });
        return nextQueue;
      }
      break;
    }

    case "updatePafta": {
      const updateIndex = findLastIndex(
        nextQueue,
        (item) => item.type === "updatePafta" && item.paftaId === task.paftaId
      );
      if (updateIndex > -1) {
        nextQueue[updateIndex] = bumpTask(nextQueue[updateIndex], task, {
          updates: {
            ...(nextQueue[updateIndex].updates || {}),
            ...(task.updates || {}),
          },
          baseUpdatedAt:
            nextQueue[updateIndex].baseUpdatedAt ?? task.baseUpdatedAt ?? null,
        });
        return nextQueue;
      }
      break;
    }

    case "updateProjectTemplate": {
      const updateIndex = findLastIndex(
        nextQueue,
        (item) =>
          item.type === "updateProjectTemplate" && item.projectId === task.projectId
      );
      if (updateIndex > -1) {
        nextQueue[updateIndex] = bumpTask(nextQueue[updateIndex], task, {
          data: { ...(nextQueue[updateIndex].data || {}), ...(task.data || {}) },
          baseUpdatedAt:
            nextQueue[updateIndex].baseUpdatedAt ?? task.baseUpdatedAt ?? null,
        });
        return nextQueue;
      }
      break;
    }

    case "renameProject": {
      const createIndex = findLastIndex(
        nextQueue,
        (item) => item.type === "createProject" && item.projectId === task.projectId
      );
      if (createIndex > -1) {
        nextQueue[createIndex] = bumpTask(nextQueue[createIndex], task, {
          type: "createProject",
          data: { ...(nextQueue[createIndex].data || {}), ...(task.data || {}) },
        });
        return nextQueue;
      }

      const renameIndex = findLastIndex(
        nextQueue,
        (item) => item.type === "renameProject" && item.projectId === task.projectId
      );
      if (renameIndex > -1) {
        nextQueue[renameIndex] = bumpTask(nextQueue[renameIndex], task, {
          data: { ...(nextQueue[renameIndex].data || {}), ...(task.data || {}) },
          baseUpdatedAt:
            nextQueue[renameIndex].baseUpdatedAt ?? task.baseUpdatedAt ?? null,
        });
        return nextQueue;
      }
      break;
    }

    case "deleteProject": {
      const createIndex = findLastIndex(
        nextQueue,
        (item) => item.type === "createProject" && item.projectId === task.projectId
      );
      if (createIndex > -1) {
        return nextQueue.filter((item) => item.projectId !== task.projectId);
      }
      return [
        ...nextQueue.filter(
          (item) =>
            !(
              item.projectId === task.projectId &&
              ["renameProject", "updateProjectTemplate"].includes(item.type)
            )
        ),
        task,
      ];
    }

    case "deleteTeam": {
      const createIndex = findLastIndex(
        nextQueue,
        (item) => item.type === "createTeam" && item.data?.id === task.teamId
      );
      if (createIndex > -1) {
        return nextQueue.filter(
          (item) =>
            !(
              (item.type === "createTeam" && item.data?.id === task.teamId) ||
              (item.type === "updateTeam" && item.teamId === task.teamId)
            )
        );
      }
      return [
        ...nextQueue.filter(
          (item) => !(item.type === "updateTeam" && item.teamId === task.teamId)
        ),
        task,
      ];
    }

    case "deletePafta":
      return [
        ...nextQueue.filter(
          (item) => !(item.type === "updatePafta" && item.paftaId === task.paftaId)
        ),
        task,
      ];

    default:
      break;
  }

  nextQueue.push(task);
  return nextQueue;
}

function comparable(value) {
  if (value == null || value === "") return value == null ? null : "";
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.map(comparable);
  if (typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        result[key] = comparable(value[key]);
        return result;
      }, {});
  }
  const text = String(value);
  const numeric = Number(text);
  return text.trim() !== "" && Number.isFinite(numeric) ? numeric : text;
}

function valuesEqual(left, right) {
  return JSON.stringify(comparable(left)) === JSON.stringify(comparable(right));
}

export function taskMatchesServerCurrent(task, current) {
  if (!task || !current) return false;

  let expected = null;
  if (task.type === "updatePoint" || task.type === "updatePafta") {
    expected = task.updates || {};
  } else if (task.type === "updateTeam") {
    expected = task.data || {};
  } else if (task.type === "renameProject") {
    expected = { name: task.data?.name || "" };
  } else if (task.type === "updateProjectTemplate") {
    expected = { measurementTemplate: task.data?.measurementTemplate || [] };
  } else {
    return false;
  }

  return Object.entries(expected).every(([key, value]) =>
    valuesEqual(current[key], value)
  );
}

function resultUpdatedAt(result) {
  return result?.updatedAt ?? result?.project?.updatedAt ?? null;
}

function pointUpdatesFromCreate(data = {}) {
  const ignored = new Set(["id", "projectId", "paftaId", "pafta", "createdAt", "updatedAt"]);
  return Object.entries(data).reduce((result, [key, value]) => {
    if (!ignored.has(key)) result[key] = value;
    return result;
  }, {});
}

export function acknowledgeTask(queue, processedTask, result) {
  const taskIndex = queue.findIndex((item) => item.taskId === processedTask.taskId);
  if (taskIndex === -1) return queue;

  const currentTask = queue[taskIndex];
  if (Number(currentTask.revision || 1) === Number(processedTask.revision || 1)) {
    return queue.filter((_, index) => index !== taskIndex);
  }

  const nextQueue = [...queue];
  const baseUpdatedAt = resultUpdatedAt(result);
  let replacement = { ...currentTask };

  if (currentTask.type === "createPoint") {
    replacement = {
      ...currentTask,
      type: "updatePoint",
      pointId: currentTask.data?.id,
      updates: pointUpdatesFromCreate(currentTask.data),
      data: undefined,
      baseUpdatedAt,
    };
  } else if (currentTask.type === "createTeam") {
    replacement = {
      ...currentTask,
      type: "updateTeam",
      teamId: currentTask.data?.id,
      data: {
        name: currentTask.data?.name,
        members: currentTask.data?.members || [],
      },
      baseUpdatedAt,
    };
  } else if (currentTask.type === "createProject") {
    replacement = {
      ...currentTask,
      type: "renameProject",
      data: { name: currentTask.data?.name || "" },
      baseUpdatedAt,
    };
  } else if (
    ["updatePoint", "updateTeam", "updatePafta", "renameProject", "updateProjectTemplate"].includes(
      currentTask.type
    )
  ) {
    replacement.baseUpdatedAt = baseUpdatedAt;
  }

  replacement.revision = Number(currentTask.revision || 1) + 1;
  replacement.updatedAt = Date.now();
  nextQueue[taskIndex] = replacement;
  return nextQueue;
}
